import axios from 'axios';

// Use relative URL for API calls - works on any domain
const API_URL = '';

// Create axios instance with default config
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============ CLIENTES ============
export const clientesApi = {
  list: () => api.get('/clientes'),
  get: (id) => api.get(`/clientes/${id}`),
  create: (data) => api.post('/clientes', data),
  update: (id, data) => api.put(`/clientes/${id}`, data),
  delete: (id) => api.delete(`/clientes/${id}`),
  timeline: (id) => api.get(`/clientes/${id}/timeline`)
};

// ============ COLABORADORES ============
export const colaboradoresApi = {
  list: () => api.get('/colaboradores'),
  get: (id) => api.get(`/colaboradores/${id}`),
  create: (data) => api.post('/colaboradores', data),
  update: (id, data) => api.put(`/colaboradores/${id}`, data),
  delete: (id) => api.delete(`/colaboradores/${id}`),
  match: (params = {}) => api.get('/colaboradores/match', { params }),
  portalAcesso: (id) => api.post(`/colaboradores/${id}/portal-acesso`),
  removePortalAcesso: (id) => api.delete(`/colaboradores/${id}/portal-acesso`),
  ocorrenciaTipos: () => api.get('/colaboradores/ocorrencias/tipos'),
  createOcorrenciaTipo: (data) => api.post('/colaboradores/ocorrencias/tipos', data),
  deleteOcorrenciaTipo: (id) => api.delete(`/colaboradores/ocorrencias/tipos/${id}`),
  ocorrencias: (id) => api.get(`/colaboradores/${id}/ocorrencias`),
  addOcorrencia: (id, data) => api.post(`/colaboradores/${id}/ocorrencias`, data),
  deleteOcorrencia: (id, oid) => api.delete(`/colaboradores/${id}/ocorrencias/${oid}`)
};

// ============ SERVIÇOS ============
export const servicosApi = {
  list: () => api.get('/servicos'),
  get: (id) => api.get(`/servicos/${id}`),
  create: (data) => api.post('/servicos', data),
  update: (id, data) => api.put(`/servicos/${id}`, data),
  delete: (id) => api.delete(`/servicos/${id}`),
  listCategorias: () => api.get('/servicos/categorias/list'),
  createCategoria: (data) => api.post('/servicos/categorias/create', data),
  updateCategoria: (id, data) => api.put(`/servicos/categorias/${id}`, data),
  deleteCategoria: (id) => api.delete(`/servicos/categorias/${id}`)
};

// ============ ORDENS DE SERVIÇO ============
export const ordensApi = {
  list: (periodo = 'all', data = null, extraParams = {}) => api.get('/ordens', {
    params: { periodo, data, ...extraParams }
  }),
  get: (id) => api.get(`/ordens/${id}`),
  create: (data) => api.post('/ordens', data),
  update: (id, data) => api.put(`/ordens/${id}`, data),
  delete: (id) => api.delete(`/ordens/${id}`),
  reciboPdf: (id) => api.get(`/ordens/${id}/recibo-pdf`, { responseType: 'blob' })
};

// ============ FINANCEIRO ============
export const financeiroApi = {
  listTransacoes: (params = {}) => 
    api.get('/financeiro/transacoes', { params }),
  getResumo: (periodo = 'mes', dataInicio = null, dataFim = null) => 
    api.get('/financeiro/resumo', { params: { periodo, data_inicio: dataInicio, data_fim: dataFim } }),
  createTransacao: (data) => api.post('/financeiro/transacoes', data),
  updateTransacao: (id, data) => api.put(`/financeiro/transacoes/${id}`, data),
  toggleStatus: (id) => api.patch(`/financeiro/transacoes/${id}/status`),
  deleteTransacao: (id) => api.delete(`/financeiro/transacoes/${id}`),
  // Categorias
  listCategorias: (tipo = null) => api.get('/financeiro/categorias', { params: tipo ? { tipo } : {} }),
  createCategoria: (data) => api.post('/financeiro/categorias', data),
  updateCategoria: (id, data) => api.put(`/financeiro/categorias/${id}`, data),
  deleteCategoria: (id) => api.delete(`/financeiro/categorias/${id}`)
};

// ============ ORÇAMENTOS ============
export const orcamentosApi = {
  list: (status = null) => api.get('/orcamentos', { params: status ? { status } : {} }),
  get: (id) => api.get(`/orcamentos/${id}`),
  create: (data) => api.post('/orcamentos', data),
  update: (id, data) => api.put(`/orcamentos/${id}`, data),
  delete: (id) => api.delete(`/orcamentos/${id}`),
  fechar: (id) => api.post(`/orcamentos/${id}/fechar`)
};

// ============ PIX ============
export const pixApi = {
  listKeys: () => api.get('/pix/keys'),
  createKey: (data) => api.post('/pix/keys', data),
  updateKey: (id, data) => api.put(`/pix/keys/${id}`, data),
  deleteKey: (id) => api.delete(`/pix/keys/${id}`),
  listCobrancas: () => api.get('/pix/cobrancas'),
  getCobranca: (id) => api.get(`/pix/cobrancas/${id}`),
  createCobranca: (data) => api.post('/pix/cobrancas', data),
  cobrancaForOrdem: (ordemId) => api.post(`/pix/cobrancas/for-ordem/${ordemId}`),
  deleteCobranca: (id) => api.delete(`/pix/cobrancas/${id}`)
};

// ============ USUÁRIOS (admin) ============
export const usuariosApi = {
  list: () => api.get('/usuarios'),
  create: (data) => api.post('/usuarios', data),
  update: (id, data) => api.put(`/usuarios/${id}`, data),
  delete: (id) => api.delete(`/usuarios/${id}`)
};

// ============ CIDADES DE ATENDIMENTO ============
export const cidadesApi = {
  list: () => api.get('/cidades-atendimento'),
  create: (data) => api.post('/cidades-atendimento', data),
  update: (id, data) => api.put(`/cidades-atendimento/${id}`, data),
  delete: (id) => api.delete(`/cidades-atendimento/${id}`)
};

// ============ LEADS ============
export const leadsApi = {
  list: (params = {}) => api.get('/leads', { params }),
  get: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  marcarConvertido: (id, orcamentoId) => api.post(`/leads/${id}/marcar-convertido`, null, { params: { orcamento_id: orcamentoId } }),
  reabrir: (id) => api.post(`/leads/${id}/reabrir`),
  addTentativa: (id, observacao = '') => api.post(`/leads/${id}/tentativa`, { observacao }),
  registrarContato: (id, data) => api.post(`/leads/${id}/registrar-contato`, data),
  followups: () => api.get('/leads/followups'),
  relatorioMotivosPerda: (params = {}) => api.get('/leads/relatorio-motivos-perda', { params }),
  createPublic: (data) => api.post('/public/leads', data)
};

export const parceriasApi = {
  criar: (data) => api.post('/parceiros/public/parcerias', data),
  listar: (status = 'todas') => api.get('/parceiros/parcerias', { params: { status } }),
  aprovar: (sid) => api.post(`/parceiros/parcerias/${sid}/aprovar`),
  recusar: (sid, motivo = '') => api.post(`/parceiros/parcerias/${sid}/recusar`, { motivo }),
};

// ============ DASHBOARD ============
export const dashboardApi = {
  getStats: (dataInicio = null, dataFim = null) => {
    const params = (dataInicio && dataFim) ? { data_inicio: dataInicio, data_fim: dataFim } : {};
    return api.get('/dashboard/stats', { params });
  },
  getReceitaMensal: (meses = 6, dataInicio = null, dataFim = null) => {
    const params = (dataInicio && dataFim)
      ? { data_inicio: dataInicio, data_fim: dataFim }
      : { meses };
    return api.get('/dashboard/receita-mensal', { params });
  },
  getReceitaPorColaborador: (meses = 3, dataInicio = null, dataFim = null) => {
    const params = (dataInicio && dataFim)
      ? { data_inicio: dataInicio, data_fim: dataFim }
      : { meses };
    return api.get('/dashboard/receita-por-colaborador', { params });
  },
  getHeatmapOS: (meses = 3, dataInicio = null, dataFim = null) => {
    const params = (dataInicio && dataFim)
      ? { data_inicio: dataInicio, data_fim: dataFim }
      : { meses };
    return api.get('/dashboard/heatmap-os', { params });
  },
  getFunilComercial: (dataInicio = null, dataFim = null) => {
    const params = (dataInicio && dataFim) ? { data_inicio: dataInicio, data_fim: dataFim } : {};
    return api.get('/dashboard/funil-comercial', { params });
  }
};

// ============ PARCEIROS (admin) ============
export const parceirosApi = {
  stats: () => api.get('/parceiros/stats'),
  list: () => api.get('/parceiros'),
  get: (id) => api.get(`/parceiros/${id}`),
  create: (data) => api.post('/parceiros', data),
  update: (id, data) => api.put(`/parceiros/${id}`, data),
  delete: (id) => api.delete(`/parceiros/${id}`),
  getConfig: () => api.get('/parceiros/config'),
  updateConfig: (data) => api.put('/parceiros/config', data),
  listUsuarios: (id) => api.get(`/parceiros/${id}/usuarios`),
  createUsuario: (id, data) => api.post(`/parceiros/${id}/usuarios`, data),
  updateUsuario: (uid, data) => api.put(`/parceiros/usuarios/${uid}`, data),
  resetSenha: (uid) => api.post(`/parceiros/usuarios/${uid}/reset-senha`),
  listImoveis: (id) => api.get(`/parceiros/${id}/imoveis`),
  listOrdens: (id) => api.get(`/parceiros/${id}/ordens`),
  createSolicitacao: (id, data) => api.post(`/parceiros/${id}/solicitacoes`, data),
  alterarLimite: (id, data) => api.put(`/parceiros/${id}/limite`, data),
  historicoLimite: (id) => api.get(`/parceiros/${id}/limite/historico`),
  listAumentos: (status = null) => api.get('/parceiros/limite-solicitacoes', { params: status ? { status } : {} }),
  analisarAumento: (sid, data) => api.post(`/parceiros/limite-solicitacoes/${sid}/analisar`, data),
  gerarFatura: (id, data) => api.post(`/parceiros/${id}/faturas/gerar`, data),
  faturamentoConfig: (id, data) => api.put(`/parceiros/${id}/faturamento-config`, data),
  listFaturas: (params = {}) => api.get('/parceiros/faturas', { params }),
  faturasDoParceiro: (id) => api.get(`/parceiros/${id}/faturas`),
  atualizarFaturaStatus: (fid, data) => api.post(`/parceiros/faturas/${fid}/status`, data),
  faturaPdf: (fid) => api.get(`/parceiros/faturas/${fid}/pdf`, { responseType: 'blob' }),
  faturasVencidas: () => api.get('/parceiros/faturas-vencidas'),
  editarFatura: (fid, data) => api.put(`/parceiros/faturas/${fid}`, data),
  excluirFatura: (fid) => api.delete(`/parceiros/faturas/${fid}`),
  pixFatura: (fid) => api.post(`/parceiros/faturas/${fid}/pix`),
  relatorio: (inicio, fim) => api.get('/parceiros/relatorio', { params: { inicio, fim } }),
  financeiro: (filtro = 'todos') => api.get('/parceiros/financeiro', { params: { filtro } }),
  uploadDocumentos: (id, files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    return api.post(`/parceiros/${id}/documentos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  listDocumentos: (id) => api.get(`/parceiros/${id}/documentos`),
  deleteDocumento: (docId) => api.delete(`/parceiros/documentos/${docId}`),
  auditoria: (id) => api.get(`/parceiros/${id}/auditoria`),
  notificacoes: (somenteNaoLidas = false) => api.get('/parceiros/notificacoes', { params: { somente_nao_lidas: somenteNaoLidas } }),
  marcarNotificacaoLida: (nid) => api.patch(`/parceiros/notificacoes/${nid}/lida`)
};

export default api;
